import { ChakraV3, MotionSlideOut } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import type { HocuspocusState } from '../hooks/useHocuspocusProvider';
import { PAGE_WIDTH } from '../utils';
import { ConnectedContent } from './ConnectedContent';
import { ConnectingContent } from './ConnectingContent';
import { DisconnectedContent } from './DisconnectedContent';
import { ErrorContent } from './ErrorContent';
import { OutdatedContent } from './OutdatedContent';

interface Props {
  documentId: string;
  state: HocuspocusState;
  isSidebarOpen: boolean;
  headerContent: React.ReactNode;
  toolbarContent: React.ReactNode;
  mainContent: React.ReactNode;
  sidebarContent?: React.ReactNode;
}

export const EditorLayout = (props: Props) => {
  const { state } = props;

  const headerContent = P.run(() => {
    switch (state.status) {
      case 'connected':
      case 'reconnecting':
        return props.headerContent;
      case 'connecting':
        return <ChakraV3.SkeletonText mt={2} mb={8} w='full' noOfLines={1} h='36px' />;
      case 'disconnected':
      case 'error':
      case 'outdated':
        return null;
    }
  });

  const toolbarContent = P.run(() => {
    switch (state.status) {
      case 'connected':
      case 'reconnecting':
        return props.toolbarContent;
      case 'connecting':
      case 'disconnected':
      case 'error':
      case 'outdated':
        return null;
    }
  });

  const mainContent = P.run(() => {
    switch (state.status) {
      case 'connected':
      case 'reconnecting':
        return (
          <ConnectedContent documentId={props.documentId}>{props.mainContent}</ConnectedContent>
        );
      case 'connecting':
        return <ConnectingContent />;
      case 'disconnected':
        return <DisconnectedContent />;
      case 'error':
        return <ErrorContent error={state.error} />;
      case 'outdated':
        return <OutdatedContent />;
    }
  });

  const sidebarContent = P.run(() => {
    if (P.isUndefined(props.sidebarContent)) {
      return null;
    }

    switch (state.status) {
      case 'connected':
      case 'reconnecting':
        return props.sidebarContent;
      case 'connecting':
      case 'disconnected':
      case 'error':
      case 'outdated':
        return (
          <ChakraV3.Box p={4}>
            <ChakraV3.SkeletonText height='6px' />
          </ChakraV3.Box>
        );
    }
  });

  return (
    <ChakraV3.Flex flexDir='row' w='full' h='full'>
      <ChakraV3.Stack w='full' gap={2}>
        <ChakraV3.Stack
          w='full'
          h='auto'
          flexShrink={0}
          maxW={PAGE_WIDTH}
          alignSelf='center'
          gap={4}
          mt={2}
          px={6}
        >
          {headerContent}
          {toolbarContent ? toolbarContent : null}
        </ChakraV3.Stack>
        {mainContent}
      </ChakraV3.Stack>

      {!P.isNull(sidebarContent) && (
        <MotionSlideOut isOpen={props.isSidebarOpen} w='300px'>
          <ChakraV3.Stack
            w='300px'
            flexShrink={0}
            borderLeftWidth='1px'
            borderColor='gray.200'
            h='full'
          >
            {sidebarContent}
          </ChakraV3.Stack>
        </MotionSlideOut>
      )}
    </ChakraV3.Flex>
  );
};
